/**
 * PlaceCorePublicView v2.2 — Mobile-first public place page.
 * Section order: Hero → Gallery → Vibe → Intents → Tags → Facts → Map → Producers → Events → DidYouKnow → Nearby → Contact → StickyActions
 * Desktop: two-column (text left, sticky sidebar right).
 *
 * Media: uses media_assets as sole source of truth.
 */
import { useMemo, useState, useCallback } from "react";
import { usePublicAccommodationsByProducers } from "@/hooks/usePublicAccommodations";
import { AccommodationSection } from "@/components/public/AccommodationSection";
import { Link } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { EXPLORE_DEBUG } from "@/lib/debug";
import { useSeasonOverlay } from "@/hooks/useSeasonOverlays";
import { useActiveSeason } from "@/hooks/useActiveSeason";
import { PlaceSeasonIndicator } from "./PlaceSeasonIndicator";
import type { HeroImage } from "@/components/place/HeroCarousel";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { PlaceFeedbackCard } from "@/components/place/PlaceFeedbackCard";
import { Seo } from "@/components/common";
import { useCoverAsset } from "@/hooks/useCoverAsset";
import { useGalleryAssets } from "@/hooks/useGalleryAssets";
import { useMediaAssetById } from "@/hooks/useMediaAssetById";
import { useMediaAssetsByIds } from "@/hooks/useMediaAssetsByIds";
import { usePlaceProducers } from "@/hooks/usePlaceProducers";
import { useNearbyPlaces } from "@/hooks/useNearbyPlaces";
import { useShare } from "@/hooks/useShare";
import { useLocale, pickLocalized } from "@/i18n";
import { useEntityTranslations } from "@/hooks/useEntityTranslations";
import {
  createPlaceJsonLd,
  buildPlaceBreadcrumbJsonLd,
  buildPlaceDescription,
} from "@/lib/seo/placeSeo";
import { getPlacePresentation } from "@/lib/placecore/public/getPlacePresentation";
import type { FarmlyPlace } from "@/lib/types";

import { PlaceCoreHero } from "./PlaceCoreHero";
import { PlaceCoreGalleryStrip } from "./PlaceCoreGalleryStrip";
import { PlaceCoreVibeSection } from "./PlaceCoreVibeSection";
import { PlaceCoreIntentsSection } from "./PlaceCoreIntentsSection";

import { PlaceCoreFactsCard } from "./PlaceCoreFactsCard";
import { PlaceCoreMapCard } from "./PlaceCoreMapCard";
import { PlaceCoreProducersSection } from "./PlaceCoreProducersSection";
import { PlaceCoreDidYouKnow } from "./PlaceCoreDidYouKnow";
import { PlaceCoreNearbyList } from "./PlaceCoreNearbyList";
import { PlaceCoreContactSection } from "./PlaceCoreContactSection";
import { PlaceCoreStickyActions } from "./PlaceCoreStickyActions";
import { PlaceCoreEventsSection } from "./PlaceCoreEventsSection";
import { PlaceContextModule } from "./PlaceContextModule";
import { PreviewBanner } from "@/components/producer/PreviewBanner";
import { PreviewHint } from "@/components/producer/PreviewHint";
import { usePrimaryArea } from "@/hooks/usePrimaryArea";
import { features } from "@/config/features";

interface Props {
  place: FarmlyPlace;
}

export function PlaceCorePublicView({ place }: Props) {
  const isMobile = useIsMobile();
  const [heroIndex, setHeroIndex] = useState(0);
  const { search } = window.location;
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const isPreviewMode = searchParams.get("preview") === "1" || searchParams.get("preview") === "true";
  const pres = useMemo(() => getPlacePresentation(place), [place]);
  const { data: primaryArea } = usePrimaryArea(place.id);
  // Must be declared before the overlay memos below, which read `locale`.
  const { locale } = useLocale();

  // --- Season overlay (priority: URL param → calendar → fallback to available overlay) ---
  const { activeSeason: currentSeason, source: seasonSource } = useActiveSeason(place.id);
  const { data: seasonOverlay } = useSeasonOverlay(place.id, currentSeason);
  const hasSeasonOverlay = !!seasonOverlay && !!(
    seasonOverlay.summary_sv || seasonOverlay.hero_media_asset_id ||
    (seasonOverlay.gallery_media_asset_ids?.length ?? 0) > 0 ||
    seasonOverlay.did_you_know_sv || seasonOverlay.typical_duration
  );

  // --- Effective overlay fields (deterministic: overlay value or fallback) ---
  // For non-sv locales, prefer the localised EN field; fall back to SV or place default.
  const effectiveDidYouKnow = useMemo(() => {
    if (locale !== 'sv' && seasonOverlay?.did_you_know_en && seasonOverlay.did_you_know_en.length >= 10) {
      return seasonOverlay.did_you_know_en;
    }
    if (seasonOverlay?.did_you_know_sv && seasonOverlay.did_you_know_sv.length >= 10) {
      return seasonOverlay.did_you_know_sv;
    }
    return pres.didYouKnow;
  }, [locale, seasonOverlay, pres.didYouKnow]);

  const effectiveBadges = useMemo(() => {
    if (!seasonOverlay?.typical_duration) return pres.badges;
    // Replace or add duration badge from seasonal overlay
    const filtered = pres.badges.filter((b) => b.type !== 'duration');
    filtered.push({ type: 'duration' as const, duration: seasonOverlay.typical_duration });
    return filtered.slice(0, 2);
  }, [seasonOverlay, pres.badges]);

  // --- Content translations (fas 2.2b — same pattern as PlaceDetail) ---
  // Disabled when locale === 'sv' (default), so Swedish renders are untouched.
  // RLS does the security filtering. See docs/SoT/localization.md §5.5.
  const { data: translations } = useEntityTranslations("place", place.id, locale);
  const localizedDescriptionShort = useMemo(
    () =>
      pickLocalized(
        place.description_short ?? "",
        translations,
        "description_short",
        locale,
      ).value,
    [place.description_short, translations, locale],
  );

  // "Så upplevs platsen" (place_core.vibe) — renders inside PlaceCoreVibeSection.
  // pres.vibeText uses a fallback chain: pc.vibe (≥80 chars) → description_short
  // (≥80 chars) → derived. We mirror that chain for the localized variant so
  // the right translation row is picked depending on which source pres.vibeText
  // resolved to. See docs/SoT/localization.md §5.5.
  const pcVibe = (place.place_core as { vibe?: string } | null)?.vibe ?? "";
  const localizedVibe = useMemo(
    () => pickLocalized(pcVibe, translations, "place_core.vibe", locale).value,
    [pcVibe, translations, locale],
  );
  const localizedVibeText = useMemo(() => {
    if (typeof pcVibe === "string" && pcVibe.length >= 80) {
      return localizedVibe || pres.vibeText;
    }
    if (place.description_short && place.description_short.length >= 80) {
      return localizedDescriptionShort || pres.vibeText;
    }
    return pres.vibeText;
  }, [pcVibe, localizedVibe, place.description_short, localizedDescriptionShort, pres.vibeText]);

  // --- Data hooks (unconditional, Rules of Hooks safe) ---
  const { producers } = usePlaceProducers(place.id);
  const { nearbyPlaces } = useNearbyPlaces({
    placeId: place.id,
    region: place.landskap ?? undefined,
    city: place.municipality ?? undefined,
    limit: 3,
  });

  // --- Media from media_assets (source of truth) ---
  const { url320: coverUrl320, url640: coverUrl, asset: coverAsset } = useCoverAsset('place', place.id);
  const { urls320: galleryUrls320, urls640: galleryUrls640, assets: galleryAssets } = useGalleryAssets('place', place.id);

  // --- Seasonal overlay media ---
  const overlayHeroId = seasonOverlay?.hero_media_asset_id ?? null;
  const { asset: overlayHeroAsset, url640: overlayHeroUrl } = useMediaAssetById(overlayHeroId);

  const overlayGalleryIds = hasSeasonOverlay ? (seasonOverlay?.gallery_media_asset_ids ?? []) : [];
  const {
    urls320: overlayGalleryUrls320,
    urls640: overlayGalleryUrls640,
  } = useMediaAssetsByIds(overlayGalleryIds);

  const useOverlayGallery = hasSeasonOverlay && overlayGalleryUrls320.length > 0;

  // Hero image: overlay hero → cover → first gallery
  const heroImageUrl = useMemo(() => {
    let chosen: string | null = null;
    let reason: string = 'none';
    if (overlayHeroUrl) { chosen = overlayHeroUrl; reason = 'overlay'; }
    else if (coverUrl) { chosen = coverUrl; reason = 'base'; }
    else if (galleryUrls640.length > 0) { chosen = galleryUrls640[0]; reason = 'gallery-fallback'; }

    if (EXPLORE_DEBUG) {
      console.debug('[PlaceCorePublicView:hero]', {
        placeId: place.id,
        season: currentSeason,
        baseCoverId: coverAsset?.id ?? null,
        overlayHeroId,
        chosenHeroId: overlayHeroAsset?.id ?? coverAsset?.id ?? null,
        reason,
      });
    }
    return chosen;
  }, [overlayHeroUrl, coverUrl, galleryUrls640, place.id, currentSeason, coverAsset, overlayHeroId, overlayHeroAsset]);

  // Shared hero/gallery media set.
  // CRITICAL: when overlay is active, ONLY overlay media are used (no base mixing).
  const sharedMedia = useMemo(() => {
    const media: { id: string; full: string; thumb: string; alt: string }[] = [];
    const seen = new Set<string>();

    const push = (
      asset: { id: string; alt_text?: string | null } | null,
      fullUrl: string | null,
      thumbUrl: string | null,
    ) => {
      if (!asset || !fullUrl || !thumbUrl || seen.has(asset.id)) return;
      seen.add(asset.id);
      media.push({
        id: asset.id,
        full: fullUrl,
        thumb: thumbUrl,
        alt: asset.alt_text ?? place.name,
      });
    };

    if (hasSeasonOverlay) {
      push(overlayHeroAsset, overlayHeroUrl, overlayHeroUrl);
      if (useOverlayGallery) {
        for (let i = 0; i < overlayGalleryIds.length; i++) {
          push(
            { id: overlayGalleryIds[i], alt_text: null },
            overlayGalleryUrls640[i] ?? null,
            overlayGalleryUrls320[i] ?? overlayGalleryUrls640[i] ?? null,
          );
        }
      }
      if (EXPLORE_DEBUG) {
        console.debug('[PlaceCorePublicView:sharedMedia] overlay-only', {
          count: media.length,
          overlayHeroId,
          overlayGalleryCount: overlayGalleryIds.length,
        });
      }
    } else {
      push(coverAsset, coverUrl, coverUrl320 ?? coverUrl);
      for (let i = 0; i < galleryAssets.length; i++) {
        push(
          galleryAssets[i],
          galleryUrls640[i] ?? null,
          galleryUrls320[i] ?? galleryUrls640[i] ?? null,
        );
      }
    }

    return media;
  }, [
    hasSeasonOverlay,
    overlayHeroAsset,
    overlayHeroUrl,
    useOverlayGallery,
    overlayGalleryIds,
    overlayGalleryUrls320,
    overlayGalleryUrls640,
    overlayHeroId,
    coverAsset,
    coverUrl,
    coverUrl320,
    galleryAssets,
    galleryUrls320,
    galleryUrls640,
    place.name,
  ]);

  const heroImages: HeroImage[] = useMemo(
    () => sharedMedia.map((m) => ({ id: m.id, src: m.full, alt: m.alt })),
    [sharedMedia],
  );

  const galleryThumbs = useMemo(() => sharedMedia.map((m) => m.thumb), [sharedMedia]);
  const galleryFull = useMemo(() => sharedMedia.map((m) => m.full), [sharedMedia]);

  const hasProducers = producers && producers.length > 0;
  const producerIds = useMemo(() => producers.map((p) => p.id), [producers]);
  const { data: placeAccommodations = [] } = usePublicAccommodationsByProducers(producerIds, 3);

  // --- SEO ---
  const canonicalUrl = useMemo(
    () => `${window.location.origin}/place/${place.slug}`,
    [place.slug],
  );

  const seoTitle = `${place.name} – plats i ${pres.regionLabel}`;
  const seoDescription = useMemo(() => buildPlaceDescription(place), [place]);

  const jsonLd = useMemo(
    () => [
      createPlaceJsonLd(place, canonicalUrl),
      buildPlaceBreadcrumbJsonLd(place, canonicalUrl),
    ],
    [place, canonicalUrl],
  );

  // --- Google Maps ---
  const googleMapsUrl = useMemo(() => {
    if (place.lat && place.lng)
      return `https://www.google.com/maps?q=${place.lat},${place.lng}`;
    const query = place.address_text || `${place.name} ${place.municipality || ""}`;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  }, [place]);

  // --- Share ---
  const { share } = useShare();
  const handleShare = () =>
    share({
      kind: "place",
      name: place.name,
      slug: place.slug,
      municipality: place.municipality,
      landskap: place.landskap,
    });

  const hasCoordinates = Boolean(place.lat && place.lng);
  const hasContact = Boolean(pres.contact.website || pres.contact.instagram || pres.contact.phone);

  return (
    <div className="min-h-dvh flex flex-col bg-background">
      <Seo
        title={seoTitle}
        description={seoDescription}
        canonicalUrl={canonicalUrl}
        ogType="place"
        image={heroImageUrl}
        imageAlt={`${place.name} – omslagsbild`}
        jsonLd={jsonLd}
      />
      <SiteHeader density="compact" />

      {isPreviewMode && (
        <PreviewBanner portalRoute={`/app/editor/places/${place.id}/edit`} />
      )}

      <main className="flex-1 pb-20 md:pb-0">
        {/* 1. Hero */}
        <PlaceCoreHero
          title={pres.title}
          subtitle={pres.subtitle}
          descriptionShort={localizedDescriptionShort || place.description_short}
          heroImageUrl={heroImageUrl}
          heroImages={heroImages}
          placeId={place.id}
          placeSlug={place.slug}
          placeName={place.name}
          onShare={handleShare}
          activeIndex={isMobile ? heroIndex : undefined}
          onHeroIndexChange={setHeroIndex}
        />

        {/* Primary area context */}
        {primaryArea && (
          <div className="container pt-3 pb-1">
            <p className="text-sm text-muted-foreground">
              Del av{" "}
              <Link to={`/omraden/${primaryArea.slug}`} className="font-medium text-foreground/80 hover:text-foreground transition-colors underline underline-offset-2 decoration-border">
                {primaryArea.name}
              </Link>
            </p>
          </div>
        )}

        {isPreviewMode && !place.description_short && (
          <div className="container pt-3">
            <PreviewHint>
              Tips: Lägg till en kort sammanfattning för bättre ingress och delning.
            </PreviewHint>
          </div>
        )}

        {/* Desktop two-column layout */}
        <div className="container">
          <div className="lg:grid lg:grid-cols-[1fr_320px] lg:gap-10 max-w-5xl">
            {/* Left column — editorial content */}
            <div className="min-w-0 space-y-8 pt-6">
              {/* 2. Gallery strip */}
              <PlaceCoreGalleryStrip
                thumbUrls={galleryThumbs}
                fullUrls={galleryFull}
                activeHeroIndex={isMobile ? heroIndex : undefined}
                onSelectIndex={isMobile ? setHeroIndex : undefined}
              />

              {/* Season indicator (only when overlay exists). Hidden when the
                  overlay is a fallback from another season — "Säsong just nu"
                  would then name the wrong season. */}
              {hasSeasonOverlay && seasonSource !== 'fallback' && (
                <PlaceSeasonIndicator season={currentSeason} />
              )}

              {/* 3. Vibe — pick season overlay text by locale (Fas 2.4).
                  sv: summary_sv takes priority over default vibe.
                  en: summary_en takes priority; falls back to localizedVibeText.
                  Localized vibe text uses content_translations when available. */}
              {(() => {
                const overlayText =
                  locale === 'sv'
                    ? seasonOverlay?.summary_sv
                    : (seasonOverlay?.summary_en || null);
                const vibeText = overlayText || localizedVibeText;
                return vibeText ? (
                  <PlaceCoreVibeSection
                    text={vibeText}
                    verified={pres.verified}
                    badges={effectiveBadges}
                  />
                ) : null;
              })()}

              {/* 4. Intents */}
              <PlaceCoreIntentsSection chips={pres.intents} />


              {/* 6. Facts (mobile only — desktop in sidebar) */}
              {pres.factsRows.length > 0 && (
                <div className="lg:hidden">
                  <PlaceCoreFactsCard factsRows={pres.factsRows} />
                </div>
              )}

              {/* 7. Map (mobile only — desktop in sidebar) */}
              <div className="lg:hidden">
                <PlaceCoreMapCard
                  mapTitle={pres.mapTitle}
                  mapSubtitle={pres.mapSubtitle}
                  hasCoordinates={hasCoordinates}
                  googleMapsUrl={googleMapsUrl}
                />
              </div>

              {/* 8. Producers (secondary) */}
              <PlaceCoreProducersSection producers={producers} />

              {/* 8b. Det händer här — upcoming events */}
              <PlaceCoreEventsSection producerIds={producerIds} />

              {/* 8c. Bo här — accommodation */}
              <AccommodationSection accommodations={placeAccommodations} title="Bo här" />

              {/* 9. Did you know */}
              {effectiveDidYouKnow && (
                <PlaceCoreDidYouKnow text={effectiveDidYouKnow} />
              )}

              {/* 9b. Context module — "Gör mer av det här" (Sammanhang) */}
              {features.showPlaceContextModule && (
                <PlaceContextModule
                  placeId={place.id}
                />
              )}

              {/* 10. Nearby */}
              <PlaceCoreNearbyList
                places={nearbyPlaces}
                placeName={place.name}
                currentLandskap={place.landskap ?? undefined}
              />

              {/* 11. Contact (mobile only — desktop in sidebar) */}
              {hasContact && (
                <div className="lg:hidden">
                  <PlaceCoreContactSection contact={pres.contact} placeName={place.name} placeSlug={place.slug} />
                </div>
              )}
            </div>

            {/* Right column — sticky sidebar (desktop) */}
            <aside className="hidden lg:block">
              <div className="sticky top-24 space-y-4 pt-6">
                {pres.factsRows.length > 0 && (
                  <PlaceCoreFactsCard factsRows={pres.factsRows} />
                )}
                <PlaceCoreMapCard
                  mapTitle={pres.mapTitle}
                  mapSubtitle={pres.mapSubtitle}
                  hasCoordinates={hasCoordinates}
                  googleMapsUrl={googleMapsUrl}
                />
                {hasContact && (
                  <div className="rounded-xl border border-border/40 bg-card p-4">
                    <PlaceCoreContactSection contact={pres.contact} placeName={place.name} placeSlug={place.slug} />
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>

        {/* Feedback section */}
        <section className="container py-8">
          <div className="max-w-2xl">
            <PlaceFeedbackCard placeId={place.id} placeName={place.name} placeSlug={place.slug} />
          </div>
        </section>
      </main>

      <SiteFooter />

      {/* 12. Sticky bottom actions (mobile) */}
      <PlaceCoreStickyActions googleMapsUrl={googleMapsUrl} onShare={handleShare} placeId={place.id} placeSlug={place.slug} placeName={place.name} placeImagePath={heroImageUrl ?? undefined} />
    </div>
  );
}
